import { useQuery } from "@tanstack/react-query";
import { useAuthReady } from "hooks/useAuthReady";
import { request } from "hooks/useOkoApi/useOkoApi";

// The classroom's students, as id to display name.
//
// The solo-accuracy endpoint keys students by id and sends no names; the report
// header holds names and no ids. This is the lookup table that joins the two,
// on the collapsed row, before anything has been expanded.
//
// Session History lists many sessions from the same handful of classrooms, so
// one query key per classroom keeps that to one request each — often already
// warm from the filter bar, which reads the same endpoint.

const EMPTY = {};

const UNKNOWN_STUDENT = "Unknown Student";

const studentsUrl = (classroomId, educatorId) => {
  const params = new URLSearchParams();
  // A session with no classroom still has scores keyed by student id. Without
  // a class the endpoint lists the educator's whole roster, which is the only
  // place those ids can be named.
  if (classroomId != null) params.set("classroom_id", classroomId);
  // Without the educator an impersonating admin's lookup returns nothing, and
  // every student silently loses their score.
  if (educatorId) params.set("educator_id", educatorId);
  const query = params.toString();
  return `account/session_history/students${query ? `?${query}` : ""}`;
};

/**
 * @param {string|number|null|undefined} classroomId
 * @param {string|null|undefined} educatorId Set when an admin is impersonating.
 * @returns {Object<string, string>} Display name by student id.
 */
export const useClassroomStudentNames = (classroomId, educatorId) => {
  const authReady = useAuthReady();

  const { data } = useQuery({
    queryKey: ["classroom-student-names", classroomId, educatorId || null],
    queryFn: () => request(studentsUrl(classroomId, educatorId), { method: "GET" }),
    enabled: authReady,
    staleTime: 5 * 60 * 1000,
  });

  if (!Array.isArray(data) || data.length === 0) return EMPTY;

  const byId = {};
  data.forEach(({ student_id: id, display_name: name }) => {
    if (id) byId[id] = name || UNKNOWN_STUDENT;
  });
  return byId;
};
